// src/pages/settings/SmsGatewayPage.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Nav,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  CheckCircle,
  Clock,
  MessageSquare,
  RefreshCcw,
  Save,
  Send,
  XCircle,
} from "react-feather";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  clearSmsGatewayMsg,
  fetchSmsGatewayThunk,
  fetchSmsSettingsThunk,
  resetSmsSettings,
  updateSmsSettingsThunk,
  updateSmsTemplateThunk,
} from "../../../store/slices/smsGatewaySlice";

import { toast } from "react-toastify";

import "../../../css/master-common.css";

// ============================================================
// HELPERS
// ============================================================

const labelize = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) =>
      c.toUpperCase()
    );

const normalizeYesNo = (value) =>
  String(value || "")
    .trim()
    .toLowerCase() === "yes"
    ? "yes"
    : "no";

const EMPTY_SETTINGS = {
  bill_generation_sms: "no",
  receipt_generation_sms: "no",
  payment_reminder_sms: "no",
};

// ============================================================
// PAGE
// ============================================================

export default function SmsGatewayPage() {
  const dispatch = useDispatch();

  // ==========================================================
  // TENANT
  // ==========================================================

  const panIds = useSelector(
    (state) =>
      state.tenant?.pan_ids || []
  );

  const panIdActive = Number(
    useSelector(
      (state) =>
        state.tenant?.pan_id_active ||
        0
    )
  );

  const panName = useSelector(
    (state) =>
      state.tenant?.pan_name || ""
  );

  const selectedPanId = useMemo(() => {
    if (panIdActive > 0) {
      return panIdActive;
    }

    if (panIds.length === 1) {
      return Number(panIds[0]);
    }

    return 0;
  }, [
    panIdActive,
    panIds,
  ]);

  const isSinglePanchayath =
    selectedPanId > 0;

  // ==========================================================
  // REDUX
  // ==========================================================

  const {
    settings = EMPTY_SETTINGS,
    templates = [],
    logs = [],
    summary = {
      total_sms: 0,
      sent_sms: 0,
      failed_sms: 0,
      pending_sms: 0,
    },

    loading = false,
    settingsLoading = false,
    savingSettings = false,
    saving = false,
    sending = false,

    error = null,
    success = null,
  } = useSelector(
    (state) =>
      state.smsGateway || {}
  );

  // ==========================================================
  // LOCAL STATE
  // ==========================================================

  const [
    activeTab,
    setActiveTab,
  ] = useState(
    "bill_generation"
  );

  const [
    localSettings,
    setLocalSettings,
  ] = useState(
    EMPTY_SETTINGS
  );

  const [
    templateForms,
    setTemplateForms,
  ] = useState({});

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {
    dispatch(
      fetchSmsGatewayThunk(
        selectedPanId
      )
    );

    if (selectedPanId > 0) {
      dispatch(
        fetchSmsSettingsThunk(
          selectedPanId
        )
      );
    } else {
      dispatch(
        resetSmsSettings()
      );
    }
  }, [
    dispatch,
    selectedPanId,
  ]);

  // ==========================================================
  // SETTINGS -> LOCAL
  // ==========================================================

  useEffect(() => {
    setLocalSettings({
      bill_generation_sms:
        normalizeYesNo(
          settings?.bill_generation_sms
        ),

      receipt_generation_sms:
        normalizeYesNo(
          settings?.receipt_generation_sms
        ),

      payment_reminder_sms:
        normalizeYesNo(
          settings?.payment_reminder_sms
        ),
    });
  }, [settings]);

  // ==========================================================
  // TEMPLATES -> LOCAL
  // ==========================================================

  useEffect(() => {
    const next = {};

    (templates || []).forEach(
      (item) => {
        next[item.template_type] = {
          sms_template_id:
            item.sms_template_id,

          template_type:
            item.template_type,

          template_name:
            item.template_name || "",

          message_template:
            item.message_template || "",

          dlt_template_id:
            item.dlt_template_id || "",

          dlt_entity_id:
            item.dlt_entity_id || "",

          dlt_status:
            item.dlt_status ||
            "pending",

          status:
            item.status ||
            "active",
        };
      }
    );

    setTemplateForms(next);
  }, [templates]);

  // ==========================================================
  // TOAST
  // ==========================================================

  useEffect(() => {
    if (!success) return;

    toast.success(
      String(success)
    );

    dispatch(
      clearSmsGatewayMsg()
    );

    dispatch(
      fetchSmsGatewayThunk(
        selectedPanId
      )
    );

    if (selectedPanId > 0) {
      dispatch(
        fetchSmsSettingsThunk(
          selectedPanId
        )
      );
    }
  }, [
    success,
    dispatch,
    selectedPanId,
  ]);

  useEffect(() => {
    if (!error) return;

    toast.error(
      String(error)
    );

    dispatch(
      clearSmsGatewayMsg()
    );
  }, [
    error,
    dispatch,
  ]);

  // ==========================================================
  // ACTIVE TEMPLATE
  // ==========================================================

  const activeTemplate =
    useMemo(
      () =>
        templateForms[
          activeTab
        ] || null,
      [
        templateForms,
        activeTab,
      ]
    );

  const activeSettingKey =
    useMemo(() => {
      if (
        activeTab ===
        "bill_generation"
      ) {
        return "bill_generation_sms";
      }

      if (
        activeTab ===
        "receipt_generation"
      ) {
        return "receipt_generation_sms";
      }

      if (
        activeTab ===
        "payment_reminder"
      ) {
        return "payment_reminder_sms";
      }

      return "";
    }, [activeTab]);

  // ==========================================================
  // TEMPLATE FIELD CHANGE
  // ==========================================================

  const updateTemplateField = (
    field,
    value
  ) => {
    setTemplateForms(
      (previous) => ({
        ...previous,

        [activeTab]: {
          ...previous[activeTab],

          [field]: value,
        },
      })
    );
  };

  // ==========================================================
  // SAVE SETTING
  // ==========================================================

  const saveSettings =
    async () => {
      if (!selectedPanId) {
        toast.error(
          "Please select one Panchayath"
        );

        return;
      }

      const result =
        await dispatch(
          updateSmsSettingsThunk({
            pan_id:
              selectedPanId,

            ...localSettings,
          })
        );

      if (
        updateSmsSettingsThunk
          .rejected
          .match(result)
      ) {
        toast.error(
          String(
            result?.payload ||
              "Failed to save SMS settings"
          )
        );
      }
    };

  // ==========================================================
  // SAVE TEMPLATE
  // ==========================================================

  const saveTemplate =
    async () => {
      if (
        !activeTemplate
          ?.sms_template_id
      ) {
        toast.error(
          "SMS template not found"
        );

        return;
      }

      const result =
        await dispatch(
          updateSmsTemplateThunk({
            id:
              activeTemplate
                .sms_template_id,

            data: {
              template_name:
                activeTemplate
                  .template_name,

              message_template:
                activeTemplate
                  .message_template,

              dlt_template_id:
                activeTemplate
                  .dlt_template_id,

              dlt_entity_id:
                activeTemplate
                  .dlt_entity_id,

              dlt_status:
                activeTemplate
                  .dlt_status,

              status:
                activeTemplate
                  .status,
            },
          })
        );

      if (
        updateSmsTemplateThunk
          .rejected
          .match(result)
      ) {
        toast.error(
          String(
            result?.payload ||
              "Failed to save SMS template"
          )
        );
      }
    };

  // ==========================================================
  // MANUAL SEND
  // ==========================================================

  const handleManualSend =
    () => {
      if (!selectedPanId) {
        toast.error(
          "Please select one Panchayath"
        );
        return;
      }

      if (
        localSettings[
          activeSettingKey
        ] !== "yes"
      ) {
        toast.error(
          "Enable this SMS type first"
        );
        return;
      }

      if (!activeTemplate) {
        toast.error(
          "SMS template is not configured"
        );
        return;
      }

      if (
        activeTemplate
          .dlt_status !==
        "approved"
      ) {
        toast.error(
          "DLT approval is required before sending SMS"
        );
        return;
      }

      toast.info(
        "Manual SMS selection will be connected with bill/receipt records."
      );
    };

  // ==========================================================
  // REFRESH
  // ==========================================================

  const handleRefresh =
    () => {
      dispatch(
        fetchSmsGatewayThunk(
          selectedPanId
        )
      );

      if (selectedPanId > 0) {
        dispatch(
          fetchSmsSettingsThunk(
            selectedPanId
          )
        );
      }
    };

  // ==========================================================
  // DLT BADGE
  // ==========================================================

  const getDltBadge = (
    status
  ) => {
    if (status === "approved") {
      return (
        <Badge className="sg-badge sg-badge-success">
          Approved
        </Badge>
      );
    }

    if (status === "rejected") {
      return (
        <Badge className="sg-badge sg-badge-danger">
          Rejected
        </Badge>
      );
    }

    return (
      <Badge className="sg-badge sg-badge-warning">
        Pending
      </Badge>
    );
  };

  // ==========================================================
  // TEMPLATE TAB
  // ==========================================================

  const renderTemplateTab = (
    title,
    description
  ) => {
    if (
      settingsLoading ||
      loading
    ) {
      return (
        <div className="sg-loading">
          <Spinner animation="border" />

          <div>
            Loading SMS settings...
          </div>
        </div>
      );
    }

    if (!isSinglePanchayath) {
      return (
        <div className="sg-warning">
          Please select one
          Panchayath from the
          Panchayath switcher.
        </div>
      );
    }

    const enabled =
      localSettings[
        activeSettingKey
      ] === "yes";

    return (
      <>
        {/* ===================================================
            ENABLE CARD
        =================================================== */}

        <Card className="sg-card sg-enable-card">
          <Card.Body>
            <div className="sg-enable-layout">
              <div className="sg-enable-left">
                <div className="sg-type-icon">
                  <MessageSquare
                    size={20}
                  />
                </div>

                <div>
                  <div className="sg-section-title">
                    {title}
                  </div>

                  <div className="sg-section-subtitle">
                    {description}
                  </div>

                  <div className="sg-current-status">
                    Current Status:
                    <span
                      className={
                        enabled
                          ? "sg-status-dot enabled"
                          : "sg-status-dot disabled"
                      }
                    />

                    <strong>
                      {enabled
                        ? "Enabled"
                        : "Disabled"}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="sg-enable-right">
                <Form.Group>
                  <Form.Label className="sg-label">
                    Enable SMS
                  </Form.Label>

                  <Form.Select
                    className="sg-control"
                    value={
                      localSettings[
                        activeSettingKey
                      ] || "no"
                    }
                    onChange={(e) =>
                      setLocalSettings(
                        (previous) => ({
                          ...previous,

                          [activeSettingKey]:
                            e.target
                              .value,
                        })
                      )
                    }
                  >
                    <option value="no">
                      No
                    </option>

                    <option value="yes">
                      Yes
                    </option>
                  </Form.Select>
                </Form.Group>

                <Button
                  className="sg-primary-btn mt-3"
                  onClick={
                    saveSettings
                  }
                  disabled={
                    savingSettings
                  }
                >
                  <Save size={15} />

                  {savingSettings
                    ? "Saving..."
                    : "Save Setting"}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* ===================================================
            TEMPLATE CARD
        =================================================== */}

        <Card className="sg-card">
          <Card.Header className="sg-card-header">
            <div className="sg-card-header-left">
              <div className="sg-header-icon">
                <MessageSquare
                  size={18}
                />
              </div>

              <div>
                <div className="sg-card-title">
                  SMS Template & DLT
                </div>

                <div className="sg-card-subtitle">
                  Configure the message
                  and DLT approval
                  information.
                </div>
              </div>
            </div>

            {activeTemplate
              ? getDltBadge(
                  activeTemplate
                    .dlt_status
                )
              : null}
          </Card.Header>

          <Card.Body className="sg-card-body">
            {!activeTemplate ? (
              <div className="sg-empty">
                <div className="sg-empty-icon">
                  <MessageSquare
                    size={23}
                  />
                </div>

                <strong>
                  Template not configured
                </strong>

                <span>
                  Add the SMS template
                  record in the database
                  first.
                </span>
              </div>
            ) : (
              <>
                <Row className="g-3">
                  <Col
                    lg={6}
                    md={12}
                  >
                    <Form.Group>
                      <Form.Label className="sg-label">
                        Template Name
                      </Form.Label>

                      <Form.Control
                        className="sg-control"
                        value={
                          activeTemplate
                            .template_name ||
                          ""
                        }
                        onChange={(e) =>
                          updateTemplateField(
                            "template_name",
                            e.target.value
                          )
                        }
                        placeholder="Template name"
                      />
                    </Form.Group>
                  </Col>

                  <Col
                    lg={6}
                    md={12}
                  >
                    <Form.Group>
                      <Form.Label className="sg-label">
                        DLT Template ID
                      </Form.Label>

                      <Form.Control
                        className="sg-control"
                        value={
                          activeTemplate
                            .dlt_template_id ||
                          ""
                        }
                        onChange={(e) =>
                          updateTemplateField(
                            "dlt_template_id",
                            e.target.value
                          )
                        }
                        placeholder="Enter DLT Template ID"
                      />
                    </Form.Group>
                  </Col>

                  <Col
                    lg={6}
                    md={12}
                  >
                    <Form.Group>
                      <Form.Label className="sg-label">
                        DLT Entity ID
                      </Form.Label>

                      <Form.Control
                        className="sg-control"
                        value={
                          activeTemplate
                            .dlt_entity_id ||
                          ""
                        }
                        onChange={(e) =>
                          updateTemplateField(
                            "dlt_entity_id",
                            e.target.value
                          )
                        }
                        placeholder="Enter DLT Entity ID"
                      />
                    </Form.Group>
                  </Col>

                  <Col
                    lg={3}
                    md={6}
                  >
                    <Form.Group>
                      <Form.Label className="sg-label">
                        DLT Approval
                      </Form.Label>

                      <Form.Select
                        className="sg-control"
                        value={
                          activeTemplate
                            .dlt_status ||
                          "pending"
                        }
                        onChange={(e) =>
                          updateTemplateField(
                            "dlt_status",
                            e.target.value
                          )
                        }
                      >
                        <option value="pending">
                          Pending
                        </option>

                        <option value="approved">
                          Approved
                        </option>

                        <option value="rejected">
                          Rejected
                        </option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col
                    lg={3}
                    md={6}
                  >
                    <Form.Group>
                      <Form.Label className="sg-label">
                        Status
                      </Form.Label>

                      <Form.Select
                        className="sg-control"
                        value={
                          activeTemplate
                            .status ||
                          "active"
                        }
                        onChange={(e) =>
                          updateTemplateField(
                            "status",
                            e.target.value
                          )
                        }
                      >
                        <option value="active">
                          Active
                        </option>

                        <option value="inactive">
                          Inactive
                        </option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="sg-label">
                        SMS Template
                      </Form.Label>

                      <Form.Control
                        className="sg-textarea"
                        as="textarea"
                        rows={5}
                        value={
                          activeTemplate
                            .message_template ||
                          ""
                        }
                        onChange={(e) =>
                          updateTemplateField(
                            "message_template",
                            e.target.value
                          )
                        }
                        placeholder="Enter SMS message template"
                      />

                      <div className="sg-variable-box">
                        <span>
                          Available variables
                        </span>

                        <div className="sg-variable-list">
                          <code>
                            {"{panchayath_name}"}
                          </code>

                          <code>
                            {"{invoice_number}"}
                          </code>

                          <code>
                            {"{receipt_number}"}
                          </code>

                          <code>
                            {"{amount}"}
                          </code>

                          <code>
                            {"{received_amount}"}
                          </code>

                          <code>
                            {"{balance_amount}"}
                          </code>
                        </div>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="sg-actions">
                  <Button
                    className="sg-outline-btn"
                    onClick={
                      handleManualSend
                    }
                    disabled={
                      sending ||
                      !enabled
                    }
                  >
                    <Send size={15} />
                    Send SMS
                  </Button>

                  <Button
                    className="sg-primary-btn"
                    onClick={
                      saveTemplate
                    }
                    disabled={
                      saving
                    }
                  >
                    <Save size={15} />

                    {saving
                      ? "Saving..."
                      : "Save Template"}
                  </Button>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </>
    );
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="container-fluid sg-page">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="sg-page-header">
        <div>
          <h4 className="sg-title">
            SMS Gateway
          </h4>

          <div className="sg-subtitle">
            Manage SMS settings,
            templates, DLT approval
            and SMS history
          </div>

          {selectedPanId > 0 &&
          panName ? (
            <div className="sg-pan-chip">
              <span>
                Panchayath
              </span>

              <strong>
                {panName}
              </strong>
            </div>
          ) : null}
        </div>

        <Button
          className="sg-refresh-btn"
          onClick={
            handleRefresh
          }
          disabled={
            loading ||
            settingsLoading
          }
        >
          <RefreshCcw
            size={16}
          />

          {loading
            ? "Refreshing..."
            : "Refresh"}
        </Button>
      </div>

      {/* =====================================================
          TABS
      ===================================================== */}

      <div className="sg-tabs-card">
        <Nav
          className="sg-tabs"
          activeKey={
            activeTab
          }
          onSelect={(key) =>
            setActiveTab(
              key ||
                "bill_generation"
            )
          }
        >
          <Nav.Item>
            <Nav.Link
              eventKey="bill_generation"
            >
              Bill Generation
            </Nav.Link>
          </Nav.Item>

          <Nav.Item>
            <Nav.Link
              eventKey="receipt_generation"
            >
              Receipt Generation
            </Nav.Link>
          </Nav.Item>

          <Nav.Item>
            <Nav.Link
              eventKey="payment_reminder"
            >
              Payment Reminder
            </Nav.Link>
          </Nav.Item>

          <Nav.Item>
            <Nav.Link
              eventKey="history"
            >
              SMS History
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </div>

      {/* =====================================================
          TABS CONTENT
      ===================================================== */}

      {activeTab ===
        "bill_generation" &&
        renderTemplateTab(
          "Bill Generation SMS",
          "Automatically notify the Panchayath when a new invoice is generated."
        )}

      {activeTab ===
        "receipt_generation" &&
        renderTemplateTab(
          "Receipt Generation SMS",
          "Automatically notify the Panchayath after a payment receipt is generated."
        )}

      {activeTab ===
        "payment_reminder" &&
        renderTemplateTab(
          "Payment Reminder SMS",
          "Enable manual payment reminders for pending Panchayath invoices."
        )}

      {/* =====================================================
          SMS HISTORY
      ===================================================== */}

      {activeTab ===
        "history" && (
        <>
          <Row className="g-3 sg-summary-row">
            <Col
              xl={3}
              md={6}
            >
              <div className="sg-summary-card">
                <div className="sg-summary-icon">
                  <MessageSquare
                    size={20}
                  />
                </div>

                <div>
                  <span>
                    Total SMS
                  </span>

                  <strong>
                    {summary
                      ?.total_sms ||
                      0}
                  </strong>
                </div>
              </div>
            </Col>

            <Col
              xl={3}
              md={6}
            >
              <div className="sg-summary-card">
                <div className="sg-summary-icon success">
                  <CheckCircle
                    size={20}
                  />
                </div>

                <div>
                  <span>
                    Sent
                  </span>

                  <strong>
                    {summary
                      ?.sent_sms ||
                      0}
                  </strong>
                </div>
              </div>
            </Col>

            <Col
              xl={3}
              md={6}
            >
              <div className="sg-summary-card">
                <div className="sg-summary-icon danger">
                  <XCircle
                    size={20}
                  />
                </div>

                <div>
                  <span>
                    Failed
                  </span>

                  <strong>
                    {summary
                      ?.failed_sms ||
                      0}
                  </strong>
                </div>
              </div>
            </Col>

            <Col
              xl={3}
              md={6}
            >
              <div className="sg-summary-card">
                <div className="sg-summary-icon warning">
                  <Clock
                    size={20}
                  />
                </div>

                <div>
                  <span>
                    Pending
                  </span>

                  <strong>
                    {summary
                      ?.pending_sms ||
                      0}
                  </strong>
                </div>
              </div>
            </Col>
          </Row>

          <Card className="sg-card">
            <Card.Header className="sg-card-header">
              <div>
                <div className="sg-card-title">
                  SMS History
                </div>

                <div className="sg-card-subtitle">
                  Latest SMS activity
                  for the selected
                  Panchayath.
                </div>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              {loading ? (
                <div className="sg-loading">
                  <Spinner />

                  <div>
                    Loading SMS history...
                  </div>
                </div>
              ) : (
                <div className="sg-table-wrap">
                  <Table
                    hover
                    className="sg-table mb-0"
                  >
                    <thead>
                      <tr>
                        <th>
                          Panchayath
                        </th>

                        <th>
                          Type
                        </th>

                        <th>
                          Mobile
                        </th>

                        <th>
                          Reference
                        </th>

                        <th>
                          Message
                        </th>

                        <th>
                          Status
                        </th>

                        <th>
                          Sent At
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {logs.length ? (
                        logs.map(
                          (item) => (
                            <tr
                              key={
                                item.sms_log_id
                              }
                            >
                              <td className="sg-pan-name">
                                {item.pan_name ||
                                  `#${item.pan_id}`}
                              </td>

                              <td>
                                {labelize(
                                  item.template_type
                                )}
                              </td>

                              <td>
                                {
                                  item.mobile_no
                                }
                              </td>

                              <td>
                                {item.reference_type ||
                                  "-"}

                                {item.reference_id
                                  ? ` #${item.reference_id}`
                                  : ""}
                              </td>

                              <td className="sg-message-cell">
                                {item.message ||
                                  "-"}
                              </td>

                              <td>
                                <Badge
                                  className={`sg-badge ${
                                    item.status ===
                                    "sent"
                                      ? "sg-badge-success"
                                      : item.status ===
                                          "failed"
                                        ? "sg-badge-danger"
                                        : "sg-badge-warning"
                                  }`}
                                >
                                  {labelize(
                                    item.status
                                  )}
                                </Badge>
                              </td>

                              <td>
                                {item.sent_at ||
                                  item.created_at ||
                                  "-"}
                              </td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td
                            colSpan={7}
                            className="sg-empty-table"
                          >
                            <div className="sg-empty-icon">
                              <MessageSquare
                                size={22}
                              />
                            </div>

                            <strong>
                              No SMS history found
                            </strong>

                            <span>
                              SMS transactions
                              will appear here.
                            </span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
}